import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const components: Components = {
  p: ({ children }) => (
    <p className="text-[13px] leading-[1.55] text-primary first:mt-0 last:mb-0 my-2 break-words hyphens-auto">
      {children}
    </p>
  ),
  strong: ({ children }) => <strong className="font-semibold text-primary">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  h1: ({ children }) => (
    <h1 className="text-[15px] font-semibold tracking-[-0.01em] text-primary mt-3 mb-2 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[14px] font-semibold tracking-[-0.01em] text-primary mt-3 mb-1.5 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-[13px] font-semibold text-primary mt-2.5 mb-1 first:mt-0">{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className="text-[13px] leading-[1.55] text-primary my-2 first:mt-0 last:mb-0 pl-5 flex flex-col gap-1 list-disc marker:text-tertiary">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="text-[13px] leading-[1.55] text-primary my-2 first:mt-0 last:mb-0 pl-5 flex flex-col gap-1 list-decimal marker:text-tertiary">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1 hyphens-auto">{children}</li>,
  hr: () => <hr className="border-0 border-t border-line-glass my-3" />,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-line pl-3 my-2 text-tertiary italic">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:no-underline"
    >
      {children}
    </a>
  ),
  code: ({ children, className }) => (
    <code
      className={`font-mono text-[12px] bg-subtle text-primary px-1 py-0.5 rounded ${className ?? ''}`}
    >
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="ai-md-pre font-mono text-[12px] bg-subtle text-primary px-2.5 py-2 rounded-md overflow-x-auto my-2">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 border border-line-glass rounded-md max-w-full">
      <table className="text-[12.5px] border-collapse" style={{ overflowWrap: 'normal' }}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-subtle">{children}</thead>,
  th: ({ children }) => (
    <th className="text-left font-semibold text-primary px-2.5 py-1.5 border-b border-line-glass whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="text-primary px-2.5 py-1.5 border-b border-line-glass align-top whitespace-nowrap">
      {children}
    </td>
  ),
  tr: ({ children }) => <tr className="last:[&>td]:border-b-0">{children}</tr>,
};

export function MarkdownContent({ children }: { children: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  );
}
